import { Dialog, DialogContent, DialogProps, DialogTitle } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import { IValidateResponse } from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React from 'react';
import { VatPercentage } from 'shared/VatPercentages';
import { FormSelectVatPercentageField } from 'shared/forms/FormSelectVatPercentageField';
import * as yup from 'yup';
import { Try } from '../Try';
import { useFormSubmit } from '../hooks/useFormSubmit';
import { useSnackbarNotify } from '../hooks/useSnackbarNotify';
import { CancelSubmitFormDialogActions } from './CancelSubmitFormDialogActions';
import { handleValidateResponse } from './utils/handleValidateResponse';

type IRequest = {
	vatPercentage: number;
};

const createSchema = (strings: IStrings, label: string) => {
	return yup.number().required(strings.formRequired(label));
};

interface IProps extends DialogProps {
	formTitle: string;
	label: string;
	notifyMessage?: string;
	initialValue: VatPercentage;
	confirm: VoidFunction;
	cancel: VoidFunction;
	submitFunction: (value: number) => Promise<Try<IValidateResponse>>;
}

export const VatPercentageModelForm = ({ notifyMessage, formTitle, initialValue, submitFunction, label, confirm, cancel, ...rest }: IProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [update, isSubmitting] = useFormSubmit(submitFunction);

	const handleSubmit = async (value: IRequest, helpers: FormikHelpers<IRequest>) => {
		const r = await update(value.vatPercentage);
		if (handleValidateResponse(r, helpers, 'vatPercentage')) {
			if (notifyMessage) {
				notify(notifyMessage);
			}
			confirm();
		}
	};

	return (
		<Formik<IRequest>
			validateOnMount
			validationSchema={createSchema(strings, label)}
			initialValues={{
				vatPercentage: initialValue,
			}}
			onSubmit={handleSubmit}>
			<Form>
				<Dialog
					fullWidth
					{...rest}>
					<DialogTitle>{strings.vatPercentage}</DialogTitle>
					<DialogContent
						dividers
						className='df-col'
						style={{ overflow: 'visible' }}>
						<FormSelectVatPercentageField<IRequest>
							name='vatPercentage'
							label={strings.vatPercentage}
							required
						/>
					</DialogContent>
					<CancelSubmitFormDialogActions
						submitText={strings.update}
						isSubmitting={isSubmitting}
						cancel={cancel}
					/>
				</Dialog>
			</Form>
		</Formik>
	);
};
